import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { BarChart, RefreshCcw, TrendingUp, TrendingDown, Download, Building2, Loader2 } from 'lucide-react';
import { categories } from '../data/categories';
import { AssessmentResult } from '../utils/scoring';
import { generatePDF } from '../utils/pdfGenerator';

interface ResultsProps {
  results: AssessmentResult | null;
  organizationData: {
    name: string;
    location: string;
    size: string;
    denomination?: string;
    contactRole: string;
    contactName: string;
    contactEmail: string;
  } | null;
  onRestart: () => void;
}

export default function Results({ results, organizationData, onRestart }: ResultsProps) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'error' | 'ready'>('loading');

  console.log('Results - Initial props:', {
    hasResults: !!results,
    hasOrgData: !!organizationData,
    results,
    organizationData
  });

  const validateData = useCallback(() => {
    // First check if we have both results and organization data
    if (!results || !organizationData) {
      console.error('Missing required data:', { 
        hasResults: !!results, 
        hasOrgData: !!organizationData 
      });
      return false;
    }

    // Validate results structure
    const hasValidResults = 
      typeof results.averageScore === 'number' &&
      typeof results.totalScore === 'number' &&
      Array.isArray(results.categoryScores) &&
      results.categoryScores.length > 0;

    // Validate organization data
    const hasValidOrgData = 
      organizationData.name &&
      organizationData.location &&
      organizationData.size &&
      organizationData.contactName &&
      organizationData.contactEmail;

    return hasValidResults && hasValidOrgData;
  }, [results, organizationData]);

  useEffect(() => {
    let mounted = true;
    
    // Initial check
    const isValid = validateData();
    console.log('Results - Validation check:', {
      isValid,
      loadingState,
      sessionStorage: !!sessionStorage.getItem('assessmentState')
    });

    if (mounted) {
      setLoadingState(isValid ? 'ready' : 'error');
    }
    
    // Try to recover from session storage if needed
    if (!isValid) {
      try {
        const savedState = sessionStorage.getItem('assessmentState');
        if (savedState) {
          console.log('Results - Attempting to recover from session storage:', {
            savedState: JSON.parse(savedState)
          });
          const parsed = JSON.parse(savedState);
          if (parsed.results && parsed.organizationData) {
            console.log('Recovered data from session storage');
            if (mounted) {
              setLoadingState('ready');
            }
          }
        }
      } catch (error) {
        console.error('Error recovering from session storage:', error);
        if (mounted) {
          setLoadingState('error');
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [results, organizationData, validateData]);

  // Debug logging for props
  useEffect(() => {
    console.log('Results component props:', {
      hasResults: !!results,
      hasOrgData: !!organizationData,
      results: results ? {
        totalScore: results.totalScore,
        averageScore: results.averageScore,
        categoryScores: results.categoryScores.length
      } : null,
      orgData: organizationData ? {
        name: organizationData.name,
        location: organizationData.location
      } : null
    });
  }, [results, organizationData]);

  // Enhanced validation check with logging
  // More detailed validation of results data
  const validationStatus = useMemo(() => {
    if (!results || !organizationData) {
      const errors = {
        results: !results ? 'Missing results' : null,
        orgData: !organizationData ? 'Missing organization data' : null
      };
      console.error('Results validation failed:', errors);
      return {
        isValid: false,
        errors
      };
    }

    // Validate required results properties
    const hasRequiredProps = 
      results &&
      typeof results.averageScore === 'number' &&
      typeof results.totalScore === 'number' &&
      Array.isArray(results.categoryScores) &&
      results.categoryScores.length > 0;

    console.log('Results validation:', {
      hasResults: !!results,
      hasOrgData: !!organizationData,
      hasRequiredProps,
      averageScore: results.averageScore,
      totalScore: results.totalScore,
      categoryScores: results.categoryScores.length
    });

    return {
      isValid: hasRequiredProps,
      errors: hasRequiredProps ? null : { results: 'Invalid results structure' }
    };
  }, [results, organizationData]);

  const handleDownload = async () => {
    if (!contentRef.current || !results || !organizationData) return;
    
    setIsGeneratingPDF(true);
    try {
      await generatePDF(results, organizationData, contentRef.current);
    } catch (error) {
      console.error('PDF generation failed:', error);
      // You could add a toast notification here
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loadingState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-[#163B56] animate-spin mb-4" />
        <p className="text-gray-600">Loading your results...</p>
      </div>
    );
  }

  if (loadingState === 'error' || !validationStatus.isValid) {
    console.error('Results validation failed:', validationStatus.errors);
    return (
      <div className="text-center p-8">
        <p className="text-red-600 mb-4">Unable to load results. Please try again.</p>
        <button
          onClick={onRestart}
          className="px-4 py-2 bg-[#163B56] text-white rounded-lg hover:bg-[#0D2436]"
          >
          Start Over
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8" ref={contentRef}>
      {/* Assessment Header */}
      {organizationData && (
        <div className="mb-12">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-gray-900">
                Your Church Ops Score is Ready!
              </h1>
              <p className="text-lg text-gray-600">
                Read Your Comprehensive Analysis Report Below
              </p>
            </div>
          </div>

          {/* Organization Info Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Church Details */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Church Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="text-lg font-medium text-gray-900">{organizationData.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-gray-800">{organizationData.location}</p>
                  </div>
                  {organizationData.denomination && (
                    <div>
                      <p className="text-sm text-gray-500">Denomination</p>
                      <p className="text-gray-800">{organizationData.denomination}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Size</p>
                    <p className="text-gray-800">{organizationData.size} members</p>
                  </div>
                </div>
              </div>
              
              {/* Assessment Details */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Assessment Details
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Completed By</p>
                    <p className="text-gray-900">{organizationData.contactName}</p>
                    <p className="text-sm text-gray-600">{organizationData.contactRole}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact Email</p>
                    <p className="text-gray-800">{organizationData.contactEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completion Date</p>
                    <p className="text-gray-800">{results.completionDate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Overall Score */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-40 h-40 rounded-full bg-indigo-100 mb-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#163B56] mb-1">
                {results?.averageScore ?? 0}%
              </div>
              <div className="text-sm font-medium text-[#0D2436] mt-2">
                {results?.totalScore ?? 0} out of {results?.maxScore ?? 0}
              </div>
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Overall Grade: {results.overallGrade.split(' ')[0]}
          </h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto">
            {results.overallGrade.split(' - ')[1]}
          </p>
          
          {/* Score Area CTA */}
          <div className="mt-8 max-w-2xl mx-auto bg-gradient-to-r from-[#163B56]/5 to-[#163B56]/10 rounded-xl p-6">
            <h3 className="text-xl font-bold text-[#163B56] mb-3">
              Want to improve your Church Ops Score?
            </h3>
            <p className="text-gray-600 mb-4">
              ShepherdOps can help. We install proven operational systems that bring structure for your church and margin for your ministry in the next 30 days.
            </p>
            <a
              href="https://meetings.hubspot.com/jon1041"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-[#163B56] text-white font-medium rounded-lg hover:bg-[#0D2436] transition-colors"
            >
              Schedule Your Free Call
            </a>
          </div>
        </div>

        {/* Category Scores */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800">Category Breakdown</h3>
          {results.categoryScores.map((cat) => {
            const category = categories.find(c => c.id === cat.category);
            const percentage = Math.round((cat.score / 50) * 100); // Each category has 5 questions, max 10 points each
            return (
              <div key={cat.category} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">{category?.title}</span>
                  <div className="text-right space-x-2">
                    <span className="text-[#163B56] font-semibold">
                      {cat.score}/{cat.maxScore} ({cat.percentage}%)
                    </span>
                    <span className="text-sm text-gray-500 ml-2">
                      Grade: {cat.grade.split(' ')[0]}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-[#163B56] h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Strengths & Growth Areas */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Strengths */}
          <div className="bg-green-50 p-6 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-800">Areas of Excellence</h3>
            </div>
            <div className="space-y-2">
              {results.strengths.length > 0 ? (
                results.strengths.map((strength) => {
                  const category = categories.find(c => c.id === strength.category);
                  return (
                    <div key={strength.category} className="text-green-700">
                      • {category?.title} - {strength.score}/50 points (Grade: {strength.grade.split(' ')[0]})
                    </div>
                  );
                })
              ) : (
                <p className="text-green-700 italic">
                  Focus on improving your scores to develop areas of excellence
                </p>
              )}
            </div>
          </div>

          {/* Growth Areas */}
          <div className="bg-amber-50 p-6 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-800">Growth Opportunities</h3>
            </div>
            <div className="space-y-2">
              {results.growthAreas.map((area) => {
                const category = categories.find(c => c.id === area.category);
                return (
                  <div key={area.category} className="text-amber-700">
                    • {category?.title} - {area.score}/50 points (Grade: {area.grade.split(' ')[0]})
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="space-y-4 pt-6">
          <button
            onClick={handleDownload}
            disabled={isGeneratingPDF}
            className="w-full px-6 py-3 bg-[#C99842] text-white font-medium rounded-lg hover:bg-[#B88832] transition-colors inline-flex items-center justify-center disabled:opacity-75 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Download Full Report
              </>
            )}
          </button>
          <button
            onClick={onRestart}
            className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center justify-center"
          >
            <RefreshCcw className="w-5 h-5 mr-2" />
            Start New Assessment
          </button>
        </div>
      </div>
      
      {/* Call to Action Section */}
      <div className="mt-16 bg-gradient-to-br from-[#163B56] to-[#0D2436] rounded-2xl p-8 text-white shadow-xl">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            Strengthen Your Church Operations So You Can Focus on Your Primary Ministry Calling
          </h2>
          
          <p className="text-lg mb-8 text-white/90">
            Get proven operational systems that bring structure for your church and margin for your ministry in the next 30 days:
          </p>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white">✓</span>
              </div>
              <p className="text-white/90">
                Without hiring a full-time operations director... that drains your limited budget
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white">✓</span>
              </div>
              <p className="text-white/90">
                Without becoming an operations expert yourself... that pulls you away from your spiritual calling
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white">✓</span>
              </div>
              <p className="text-white/90">
                Without adding more part-time staff... that creates communication gaps and inconsistency
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white">✓</span>
              </div>
              <p className="text-white/90">
                Without settling for "good enough" operations... that limit your church's growth potential
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-lg mb-6">
              Don't wait. Book your free Discovery Call to see if partnering with ShepherdOps is the right fit for your church.
            </p>
            
            <a
              href="https://meetings.hubspot.com/jon1041"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-[#163B56] font-semibold rounded-lg hover:bg-white/90 transition-colors"
            >
              Schedule a Call Today!
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}